import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import language from '../../variables/language';
import * as api from './api';
import { buildElementPayload } from './exerciseTemplatePayload';
import '../../css/exercise-create-modal.css';

// Paste a screenshot (Ctrl+V, or the "read clipboard" button) and get its text
// straight away. The text stays editable and can be copied or added to the
// exercise as a text block. Recognition is the local OCR endpoint.
export default function ImageClipboardTool({ exerciseId, position, onAdded }) {
   const t = language[language.lang].screenshotImport.imageTool;
   const [open, setOpen] = useState(false);
   const [status, setStatus] = useState('idle');
   const [text, setText] = useState('');
   const [notice, setNotice] = useState('');
   const [busy, setBusy] = useState(false);
   const zoneRef = useRef(null);

   useEffect(() => {
      if (open && zoneRef.current) zoneRef.current.focus();
   }, [open]);

   const recognize = (file) => {
      if (!file) return;
      setStatus('loading');
      setNotice('');
      const form = new FormData();
      form.append('file', file, 'clipboard.png');
      axios.post('/api/exercise-ocr/recognize', form, { withCredentials: true })
         .then(response => {
            setText((response.data && response.data.text) || '');
            setStatus('done');
         })
         .catch(() => {
            setStatus('error');
            setNotice(t.failed);
         });
   };

   const imageFrom = (items) => {
      const list = Array.from(items || []);
      const item = list.find(i => i.type && i.type.startsWith('image/'));
      return item ? item.getAsFile() : null;
   };

   const onPaste = (e) => {
      const file = imageFrom(e.clipboardData && e.clipboardData.items);
      if (file) {
         e.preventDefault();
         recognize(file);
      } else {
         setNotice(t.noImage);
      }
   };

   const readClipboard = () => {
      if (!navigator.clipboard || !navigator.clipboard.read) {
         setNotice(t.zone);
         return;
      }
      navigator.clipboard.read()
         .then(async (items) => {
            for (const item of items) {
               const imageType = item.types.find(type => type.startsWith('image/'));
               if (imageType) {
                  const blob = await item.getType(imageType);
                  recognize(new File([blob], 'clipboard.png', { type: imageType }));
                  return;
               }
            }
            setNotice(t.noImage);
         })
         .catch(() => setNotice(t.zone));
   };

   const close = () => {
      setOpen(false);
      setStatus('idle');
      setText('');
      setNotice('');
   };

   const copy = () => {
      if (!text || !navigator.clipboard || !navigator.clipboard.writeText) return;
      navigator.clipboard.writeText(text).catch(() => {});
   };

   const addAsText = () => {
      if (!text.trim() || busy) return;
      setBusy(true);
      const draft = { type: 'text', text: text.trim(), config: '{}', position };
      api.createElement(buildElementPayload(exerciseId, draft))
         .then(() => {
            setNotice(t.added);
            setText('');
            setStatus('idle');
            if (onAdded) onAdded();
         })
         .catch(() => setNotice(t.failed))
         .finally(() => setBusy(false));
   };

   return (
      <>
         <button type='button' className='ecm-tool-btn' onClick={() => setOpen(true)}>
            <span className='material-symbols-outlined'>content_paste</span>
            {t.button}
         </button>
         {open ? (
            <div className='ecm-overlay' onClick={close}>
               <div className='ecm-panel ecm-panel-small' onClick={e => e.stopPropagation()}>
                  <header className='ecm-header'>
                     <h3 className='ecm-title'>
                        <span className='material-symbols-outlined ecm-title-icon'>content_paste</span>
                        {t.title}
                     </h3>
                     <button type='button' className='ecm-close' onClick={close} aria-label={t.close}>×</button>
                  </header>
                  <div className='ecm-body'>
                     <div ref={zoneRef} tabIndex={0} className={'ecm-paste-zone' + (status === 'loading' ? ' is-busy' : '')} onPaste={onPaste}>
                        <span className='material-symbols-outlined ecm-paste-icon'>{status === 'loading' ? 'hourglass_top' : 'image'}</span>
                        <span className='ecm-paste-text'>{status === 'loading' ? t.recognizing : t.zone}</span>
                        <button type='button' className='ecm-btn' onClick={(e) => { e.stopPropagation(); readClipboard(); }}>
                           <span className='material-symbols-outlined ecm-btn-icon'>content_paste_go</span>
                           {t.readClipboard}
                        </button>
                     </div>
                     {notice ? <p className='ecm-hint ecm-notice'>{notice}</p> : ''}
                     {status === 'done' ? (
                        <textarea className='ecm-textarea ecm-result' rows={8} value={text} onChange={e => setText(e.target.value)} />
                     ) : ''}
                  </div>
                  {status === 'done' ? (
                     <footer className='ecm-footer'>
                        <button type='button' className='ecm-btn' onClick={copy} disabled={!text}>
                           <span className='material-symbols-outlined ecm-btn-icon'>content_copy</span>
                           {t.copy}
                        </button>
                        <button type='button' className='ecm-btn ecm-btn-primary' onClick={addAsText} disabled={!text.trim() || busy}>
                           <span className='material-symbols-outlined ecm-btn-icon'>notes</span>
                           {t.addAsText}
                        </button>
                     </footer>
                  ) : ''}
               </div>
            </div>
         ) : ''}
      </>
   );
}
